/**
 * Global mint listener context.
 *
 * Starts the mint's Nostr listener at the app level so it runs
 * regardless of which page the user is on. This allows the wallet
 * page to deposit to a mint in the same browser tab.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useMintListener } from '@/hooks/useMintListener';
import { isMintSetupComplete } from '@/lib/storage/mint-store';

interface MintListenerContextType {
  listening: boolean;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
}

const MintListenerContext = createContext<MintListenerContextType>({
  listening: false,
  startListening: async () => {},
  stopListening: async () => {},
});

export function MintListenerProvider({ children }: { children: ReactNode }) {
  const { listening, startListening, stopListening } = useMintListener();

  // Auto-start when mint setup is complete
  useEffect(() => {
    let mounted = true;
    isMintSetupComplete().then((ready) => {
      if (ready && mounted && !listening) {
        startListening();
      }
    });
    return () => { mounted = false; };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MintListenerContext.Provider value={{ listening, startListening, stopListening }}>
      {children}
    </MintListenerContext.Provider>
  );
}

export function useMintListenerContext(): MintListenerContextType {
  return useContext(MintListenerContext);
}
